import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader2, XCircle, Package } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { API } from '../config/api';

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        clearCart();
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Payment status error:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  return (
    <div className="min-h-screen py-16 px-4 flex items-center justify-center">
      <div className="max-w-md text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="mx-auto text-[#D4AF37] animate-spin mb-6" size={48} />
            <h1 className="font-serif text-2xl text-[#F5F5F0] mb-4">Processing Payment...</h1>
            <p className="text-[#A1A1AA]">Please wait while we confirm your purchase.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="mx-auto text-[#10B981] mb-6" size={48} />
            <h1 className="font-serif text-2xl text-[#F5F5F0] mb-4">Purchase Complete!</h1>
            <p className="text-[#A1A1AA] mb-8">
              Thank you for your purchase! You now have full access to your artwork(s).
              Check your purchases to download or view the full resolution files.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/purchases" className="btn-primary flex items-center justify-center gap-2">
                <Package size={18} />
                View Purchases
              </Link>
              <Link to="/gallery" className="btn-secondary">
                Continue Browsing
              </Link>
            </div>
          </>
        )}

        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <>
            <XCircle className="mx-auto text-[#EF4444] mb-6" size={48} />
            <h1 className="font-serif text-2xl text-[#F5F5F0] mb-4">
              {status === 'timeout' ? 'Payment Verification Timeout' : 'Payment Failed'}
            </h1>
            <p className="text-[#A1A1AA] mb-8">
              {status === 'timeout' 
                ? 'We couldn\'t verify your payment. Please check your email for confirmation.'
                : 'There was an issue processing your payment. Please try again.'}
            </p>
            <Button onClick={() => navigate('/cart')} className="btn-primary">
              Return to Cart
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
